// WHAT IS THIS FILE? — client-side auto-detection of a golden-CSV `kind`.
//
// The drop point used to make the owner hand-classify every export in a "What is this file?"
// dropdown. His reaction was, verbatim: *"Can you not work that out yourself???"* Mostly, yes:
// the files carry their own identity in their header row, their FE preamble and their
// entity_ref/metric vocabulary. This module reads that identity and SUGGESTS a kind.
//
// ⚠️ IT SUGGESTS. IT NEVER DECIDES. ⚠️
// reporting-golden-ingest dedupes on UNIQUE(period_label, kind, content_sha256) — the kind is
// part of a file's IDENTITY. A mis-detected file therefore lands in the WRONG bucket under a
// hash that looks legitimately ingested, and nothing downstream can tell the difference. So the
// contract of this module is:
//   * 'certain'   — a signal that is unique to one kind BY CONSTRUCTION (a header column only
//                   that kind has; a metric key only that kind emits). Pre-select it.
//   * 'likely'    — a shape/naming heuristic that is usually right. Pre-select it, but the UI
//                   must visibly mark it as a guess that wants a glance.
//   * 'ambiguous' — `kind` is NULL and stays null. Say what was ambiguous and stop. Guessing to
//                   be helpful is exactly the failure mode that lands a file in the wrong bucket.
// The dropdown remains, remains freely overridable, and nothing here ever auto-submits.
//
// GROUND TRUTH — every pattern below is lifted from code/contract, not invented:
//   * HEADER_CONTRACTS        supabase/functions/reporting-golden-ingest/index.ts:212
//   * sniffFeFormat           …/index.ts:813 (mirrored exactly — same banner/offset/'Name' test)
//   * FE_KINDS                …/index.ts:369 (the FE-native path serves TWO kinds and no others)
//   * FE_CLASSES crosswalk    …/index.ts:473 (the CBAM/AFI/ARC Name patterns, date-tolerant)
//   * entity_ref / metric per kind — docs/investment-reporting/golden-csv-contract-v0.md §1-§11,
//     which is where the LANDED shapes are specified (`entity_ref=BM.{level}.{bench}`,
//     `metric=lookthrough_pct`, `entity_ref='costs'`, …). Those shapes are what actually
//     separate the eight kinds that share one header contract.
//   * the 9 peer houses       services/reporting-compute/benchmarks.py:269 (COMPOSITE_Q2)
// If any of those change, this must change with them — it is a MIRROR, not a second opinion.
//
// THE HEADER PROBLEM THIS EXISTS TO SOLVE. Eight of the eleven kinds share the IDENTICAL header
// contract ["entity_ref","metric","as_of_date","value"]. Headers alone can only ever identify
// three (holdings_base_snapshot / holdings_deltas / ba_trades). Everything else has to come from
// the CONTENT of entity_ref and metric — which is fine, because those vocabularies are closed
// and disjoint per kind.
//
// Pure functions only: no React, no network, no File API. The caller reads the head of the file
// and hands over a string.

// The `kind` union is owned by the picker's vocabulary file — imported, never re-declared, so
// the detector and the dropdown cannot drift apart.
import type { GoldenKind } from "@/components/investment-reporting/labels";

// ----------------------------------------------------------------------------
// Result shape
// ----------------------------------------------------------------------------
export type DetectConfidence = "certain" | "likely" | "ambiguous";

/** Which layer produced the answer — surfaced so a wrong call can be traced to its signal. */
export type DetectSignal = "header" | "fe_native" | "content" | "filename" | "none";

export interface KindDetection {
  /** ALWAYS null when confidence is 'ambiguous'. Never guess to be helpful. */
  kind: GoldenKind | null;
  confidence: DetectConfidence;
  /** Plain English, for a human at the drop point. No jargon that is not on screen already. */
  reason: string;
  /** The kinds still in play. Non-empty when ambiguous; may also carry the runner-up. */
  alternatives: GoldenKind[];
  signal: DetectSignal;
}

export interface DetectOptions {
  /**
   * TRUE when `csvText` is only the HEAD of a larger file (the caller read a byte slice).
   * Load-bearing on the FE-native path: FE rows are ALPHABETICAL, so AFI/ARC sort ahead of
   * CBAM P1-R5 — a truncated read can show benchmark rows and no portfolio rows and look
   * deceptively unanimous. When the sample is partial we never claim 'certain' from an
   * ABSENCE.
   */
  partial?: boolean;
  /** How many CSV records to parse. Detection is a head-read, not a full parse. */
  maxLines?: number;
}

/** Detection reads the head of the file only — these are large exports. */
export const DETECT_MAX_LINES = 50;
/** A byte budget for the caller's slice. ~64 KB comfortably covers any FE Performance Table. */
export const DETECT_HEAD_BYTES = 64 * 1024;

function detection(
  kind: GoldenKind | null,
  confidence: DetectConfidence,
  reason: string,
  alternatives: GoldenKind[],
  signal: DetectSignal,
): KindDetection {
  // The one invariant worth enforcing in code rather than in a comment.
  return {
    kind: confidence === "ambiguous" ? null : kind,
    confidence,
    reason,
    alternatives: alternatives.filter((k) => k !== kind),
    signal,
  };
}

// ----------------------------------------------------------------------------
// A bounded RFC-4180 head parser. Mirrors reporting-golden-ingest's parseCsv (index.ts:257) —
// quotes, escaped quotes, CRLF, blank lines dropped — but stops after `maxRows` records so a
// 40 MB export costs 50 lines of work.
// ----------------------------------------------------------------------------
function parseCsvHead(text: string, maxRows: number): { grid: string[][]; hitRowCap: boolean } {
  const grid: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQ = false;
  let hitRowCap = false;

  const push = () => {
    row.push(field);
    field = "";
    if (!(row.length === 1 && row[0].trim() === "")) grid.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQ = false;
      } else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\r") {
      /* skip */
    } else if (c === "\n") {
      push();
      if (grid.length >= maxRows) {
        hitRowCap = true;
        return { grid, hitRowCap };
      }
    } else field += c;
  }
  if (field.length > 0 || row.length > 0) push();
  return { grid, hitRowCap };
}

function normHeader(cell: string): string {
  return (cell ?? "").replace(/^﻿/, "").trim().toLowerCase();
}

function hasAll(headers: string[], required: string[]): boolean {
  return required.every((c) => headers.includes(c));
}

// ----------------------------------------------------------------------------
// LAYER 1 — HEADER MATCH. The only three kinds a header row can identify, because the other
// eight share ["entity_ref","metric","as_of_date","value"] verbatim (HEADER_CONTRACTS,
// index.ts:212). Each of these three carries a column NO other kind has, so a hit is exact.
// ----------------------------------------------------------------------------
const HOLDINGS_COMMON = ["book", "portfolio_ref", "holding_ref"];

function detectFromHeader(headers: string[]): KindDetection | null {
  const hasBase = headers.includes("weight_base");
  const hasDelta = headers.includes("weight_delta");
  const hasEventDate = headers.includes("event_date");
  const hasTradeDate = headers.includes("trade_date");
  const hasTradeRef = headers.includes("ba_trade_ref");

  // holdings_base_snapshot — weight_base belongs to nothing else, and the DB shape CHECK
  // (base_snapshot ⇒ weight_base set AND weight_delta null) means the two never co-occur.
  if (hasBase && !hasDelta && hasAll(headers, [...HOLDINGS_COMMON, "event_date"])) {
    return detection(
      "holdings_base_snapshot",
      "certain",
      "It has a weight_base column, which only the opening-snapshot file has.",
      [],
      "header",
    );
  }

  // holdings_deltas — weight_delta + event_date. ba_trade_ref MAY also be present here (the
  // ingest reads it as the corroborating trade reference), so it is not on its own a trade file.
  if (hasDelta && !hasBase && hasAll(headers, [...HOLDINGS_COMMON, "event_date"])) {
    return detection(
      "holdings_deltas",
      "certain",
      "It has a weight_delta column and dated events, which is the in-period changes file.",
      [],
      "header",
    );
  }

  // ba_trades — trade_date + ba_trade_ref, and NO holdings-event columns.
  if (hasTradeDate && hasTradeRef && !hasEventDate && !hasBase && !hasDelta && hasAll(headers, HOLDINGS_COMMON)) {
    return detection(
      "ba_trades",
      "certain",
      "It has trade_date and ba_trade_ref columns, which is the broker/administrator trade log.",
      [],
      "header",
    );
  }

  // Both event and trade shapes present: genuinely undecidable from the header. Say so.
  if ((hasDelta || hasBase) && hasTradeDate && hasTradeRef && hasEventDate) {
    return detection(
      null,
      "ambiguous",
      "It carries BOTH holdings-event columns and trade-log columns, so the header cannot say "
        + "which file it is.",
      ["holdings_deltas", "ba_trades"],
      "header",
    );
  }

  return null;
}

// ----------------------------------------------------------------------------
// LAYER 2 — FE-NATIVE SNIFF. Mirrors sniffFeFormat (index.ts:813) EXACTLY: line 1 is a
// single-field "Performance Table" banner and line 3 (offset 2) starts with `Name`. A file of
// this shape can only ever be one of TWO kinds — FE_KINDS (index.ts:369) restricts the
// FE-native parse path to fe_portfolio_returns and fe_benchmark_returns, and the ingest REFUSES
// every other kind on this shape rather than guessing.
//
// Note the layer ordering is not a conflict: an FE file's row 0 is the banner, so it can never
// satisfy a HEADER_CONTRACT at row 0. The two layers are disjoint by construction.
// ----------------------------------------------------------------------------
const FE_HEADER_ROW = 2;
const FE_BANNER_RX = /^\s*performance\s+table\s*$/i;

function sniffFeFormat(grid: string[][]): boolean {
  if (grid.length < FE_HEADER_ROW + 2) return false;
  const banner = grid[0];
  if (!banner || banner.length !== 1 || !FE_BANNER_RX.test(banner[0] ?? "")) return false;
  const hdr = grid[FE_HEADER_ROW];
  return !!hdr && hdr.length >= 2 && (hdr[0] ?? "").trim().toLowerCase() === "name";
}

// The FE Name crosswalk, mirrored from FE_CLASSES (index.ts:473). Anchored and DATE-TOLERANT
// like the originals — the embedded dd/mm/yyyy is a volatile series-VERSION marker, not the
// performance window, and it changes between exports.
const FE_NAME_PORTFOLIO_RX = /^CBAM\s+[PR][1-5](?:\s+\d{2}\/\d{2}\/\d{4})?\s+TR\s+in\s+GB$/i;
const FE_NAME_AFI_RX = /^AFI\s+(Cautious|Balanced|Aggressive)(?:\s+\d{2}\/\d{2}\/\d{4})?\s+TR\s+in\s+GB$/i;
const FE_NAME_ARC_RX = /^ARC\s+MPS\s+(\d{1,3})[\s\-–]+(\d{1,3})\s*%\s+Index\s+TR\s+in\s+GB$/i;
const FE_NAME_CBAM_BM_RX = /^CBAM\s+(IV|III|II|I|V)\s+Benchmark(?:\s+\d{2}\/\d{2}\/\d{4})?\s+TR\s+in\s+GB$/i;
// Recognised CBAM products that belong to NEITHER kind (klass 'out_of_scope', index.ts:590/601).
// They must not vote, or a Dist-heavy head would drag the count around.
const FE_NAME_OUT_OF_SCOPE_RX =
  /^CBAM\s+(?:Dist\s+(?:High|Med|Medium|Low)|MM)(?:\s+\d{2}\/\d{2}\/\d{4})?\s+TR\s+in\s+GB$/i;

const FE_TWO_KINDS: GoldenKind[] = ["fe_portfolio_returns", "fe_benchmark_returns"];

function detectFromFeNative(grid: string[][], partialSample: boolean): KindDetection {
  let portfolio = 0;
  let benchmark = 0;
  let outOfScope = 0;
  let unrecognised = 0;

  for (let r = FE_HEADER_ROW + 1; r < grid.length; r++) {
    const name = (grid[r]?.[0] ?? "").trim();
    if (!name) continue;
    if (FE_NAME_PORTFOLIO_RX.test(name)) portfolio++;
    else if (FE_NAME_AFI_RX.test(name) || FE_NAME_ARC_RX.test(name) || FE_NAME_CBAM_BM_RX.test(name)) benchmark++;
    else if (FE_NAME_OUT_OF_SCOPE_RX.test(name)) outOfScope++;
    else unrecognised++;
  }

  const shape = "It is a FinancialExpress Performance Table export, so it can only be portfolio "
    + "returns or benchmark returns";

  // THE NORMAL CASE, and deliberately NOT resolved. One export carries both families
  // (golden-csv-contract-v1 §8.1): the same bytes are dropped TWICE, once per kind, and
  // identical sha256 is the proof it is one file. Picking one here would quietly cost the
  // owner the other half of the cycle.
  if (portfolio > 0 && benchmark > 0) {
    return detection(
      null,
      "ambiguous",
      `${shape}. It contains BOTH: ${portfolio} portfolio row(s) (CBAM P1–R5) and ${benchmark} `
        + "benchmark row(s) (CBAM I–V / AFI / ARC). One export carries both, so it gets dropped "
        + "twice — once under each kind. Pick which one this drop is.",
      FE_TWO_KINDS,
      "fe_native",
    );
  }

  if (portfolio > 0 && benchmark === 0) {
    // An ABSENCE is only trustworthy if we read the whole file. FE rows are alphabetical, so a
    // truncated head can hide a whole family.
    const conf: DetectConfidence = partialSample ? "likely" : "certain";
    return detection(
      "fe_portfolio_returns",
      conf,
      `${shape}, and every recognised row is a CBAM P1–R5 model (${portfolio} of them), with no `
        + "benchmark rows"
        + (partialSample ? " in the part of the file that was read." : "."),
      FE_TWO_KINDS,
      "fe_native",
    );
  }

  if (benchmark > 0 && portfolio === 0) {
    const conf: DetectConfidence = partialSample ? "likely" : "certain";
    return detection(
      "fe_benchmark_returns",
      conf,
      `${shape}, and every recognised row is a benchmark (${benchmark} CBAM I–V / AFI / ARC rows), `
        + "with no CBAM P1–R5 model rows"
        + (partialSample ? " in the part of the file that was read." : "."),
      FE_TWO_KINDS,
      "fe_native",
    );
  }

  return detection(
    null,
    "ambiguous",
    `${shape} — but none of its ${unrecognised + outOfScope} row name(s) match a known CBAM, AFI `
      + "or ARC series, so there is nothing to tell the two apart.",
    FE_TWO_KINDS,
    "fe_native",
  );
}

// ----------------------------------------------------------------------------
// LAYER 3 — CONTENT of entity_ref / metric on the generic long format.
//
// This is the layer that actually separates the eight header-identical kinds, because their
// LANDED vocabularies are closed and disjoint (golden-csv-contract-v0 §1-§11):
//
//   kind                              entity_ref                       metric
//   ────────────────────────────────  ───────────────────────────────  ───────────────────────
//   fe_portfolio_returns              P1..P5 / R1..R5                  return_q2|6m|1yr
//   fe_benchmark_returns              BM.{1-5}.{afi|arc|cbam}          return_q2|6m|1yr
//   composite_constituents            {house}.{1-5}                    return_q2|6m|1yr
//   fe_fund_returns                   {fund name}                      return_q2 | return_lt | ocf
//   fe_benchmark_constituent_weights  BM.{1-5}.{bench}.{category}      weight
//   fe_sector_weights                 {house}.{1-5}.{sector}           weight
//   sector_mappings                   {fund}.{sector}                  lookthrough_pct
//   costs                             'costs'                          P_amc|R_amc|P5_total|*_range
//   ba_trades                         {trade_ref}                      ba_trade
//
// Four of those metrics are UNIQUE to one kind, which is a by-construction identification.
// The rest need entity_ref shape as well.
// ----------------------------------------------------------------------------

/** A metric that names exactly one kind and nothing else. Keys are lower-cased. */
const METRIC_ONLY_KIND: Record<string, GoldenKind> = {
  lookthrough_pct: "sector_mappings",
  ba_trade: "ba_trades",
  ocf: "fe_fund_returns",
  return_lt: "fe_fund_returns",
  p_amc: "costs",
  r_amc: "costs",
  p5_total: "costs",
  active_ocf_range: "costs",
  passive_ocf_range: "costs",
};

const METRIC_RETURN_RX = /^return_(1m|3m|q[1-4]|6m|1yr|custom)$/i;
const METRIC_WEIGHT_RX = /^(weight|weight_pct)$/i;

// entity_ref shapes. Anchored, and ordered so the longer shape is tested first.
const ENTITY_PORTFOLIO_RX = /^[PR][1-5]$/i;
const ENTITY_BM_CONSTITUENT_RX = /^BM\.[1-5]\.(afi|arc|cbam)\..+$/i;
const ENTITY_BM_RETURN_RX = /^BM\.[1-5]\.(afi|arc|cbam)$/i;
// {house}.{level}.{sector} — the level segment is what identifies it; the house and sector
// names are free text (and a fund name can itself contain a dot, e.g. "T. Rowe US Smaller
// Cos"), so never split naively on '.'.
const ENTITY_HOUSE_LEVEL_SECTOR_RX = /^(.+?)\.([1-5])\.(.+)$/;
const ENTITY_HOUSE_LEVEL_RX = /^(.+)\.([1-5])$/;
// ISO 6166: 2 letters + 9 alphanumerics + 1 check digit. NOTE — the Q2 fixture does NOT use
// ISINs (its fund identities are plain names like "Fidelity Index US"), so this is a
// high-precision extra, not the path that normally fires.
const ENTITY_ISIN_RX = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;

// The nine peer houses. MIRRORS services/reporting-compute/benchmarks.py:269 COMPOSITE_Q2 and
// docs/investment-reporting/fixtures/q2-2026-cycle.json data.composite_constituents.houses.
// Used ONLY to promote a shape match to an exact one — an unrecognised house still resolves,
// just one confidence step lower. It is never a membership gate.
const PEER_HOUSES = [
  "7im aap",
  "aviva ai multi-asset",
  "blackrock consensus",
  "invesco summit",
  "l&g multi-index",
  "liontrust ma blended",
  "santander atlas",
  "scottish widows horizon",
  "vanguard lifestrategy",
];

type RowVerdict = { kind: GoldenKind; exact: boolean } | null;

function classifyRow(entityRaw: string, metricRaw: string): RowVerdict {
  const entity = (entityRaw ?? "").trim();
  const metric = (metricRaw ?? "").trim().toLowerCase();

  // 3a. A metric that belongs to exactly one kind settles it outright.
  const only = METRIC_ONLY_KIND[metric];
  if (only) return { kind: only, exact: true };
  if (entity.toLowerCase() === "costs") return { kind: "costs", exact: true };

  // 3b. Weights: two kinds emit `weight`, and the entity_ref namespace separates them.
  if (METRIC_WEIGHT_RX.test(metric)) {
    if (ENTITY_BM_CONSTITUENT_RX.test(entity)) {
      return { kind: "fe_benchmark_constituent_weights", exact: true };
    }
    const hls = entity.match(ENTITY_HOUSE_LEVEL_SECTOR_RX);
    if (hls) {
      return { kind: "fe_sector_weights", exact: PEER_HOUSES.includes(hls[1].trim().toLowerCase()) };
    }
    return null;
  }

  // 3c. Returns: four kinds emit return_*, and the entity_ref shape separates them.
  if (METRIC_RETURN_RX.test(metric) || metric === "") {
    if (ENTITY_PORTFOLIO_RX.test(entity)) return { kind: "fe_portfolio_returns", exact: true };
    if (ENTITY_BM_CONSTITUENT_RX.test(entity)) {
      return { kind: "fe_benchmark_constituent_weights", exact: true };
    }
    if (ENTITY_BM_RETURN_RX.test(entity)) return { kind: "fe_benchmark_returns", exact: true };
    const hl = entity.match(ENTITY_HOUSE_LEVEL_RX);
    if (hl) {
      return { kind: "composite_constituents", exact: PEER_HOUSES.includes(hl[1].trim().toLowerCase()) };
    }
    if (ENTITY_ISIN_RX.test(entity)) return { kind: "fe_fund_returns", exact: true };
    // Free-text identity with a return metric = a fund. This is the FALLBACK bucket, so it is
    // never `exact` — "it looks like nothing else" is not the same as "it is this".
    if (entity) return { kind: "fe_fund_returns", exact: false };
  }

  return null;
}

/** Human-readable evidence: up to 3 distinct example values, quoted. */
function samples(values: string[], n = 3): string {
  const seen: string[] = [];
  for (const v of values) {
    const t = (v ?? "").trim();
    if (t && !seen.includes(t)) seen.push(t);
    if (seen.length >= n) break;
  }
  return seen.map((s) => `"${s}"`).join(", ");
}

function detectFromContent(
  grid: string[][],
  headers: string[],
  partialSample: boolean,
): KindDetection {
  const iEntity = headers.indexOf("entity_ref");
  const iMetric = headers.indexOf("metric");
  if (iEntity < 0) {
    return detection(
      null,
      "ambiguous",
      "Its header row has no entity_ref column and matches none of the holdings or trade "
        + "contracts, so there is nothing to identify it by.",
      [],
      "none",
    );
  }

  const counts: Partial<Record<GoldenKind, { total: number; exact: number }>> = {};
  const entityExamples: string[] = [];
  const metricExamples: string[] = [];
  let sampled = 0;
  let unclassified = 0;

  for (let r = 1; r < grid.length; r++) {
    const row = grid[r] ?? [];
    const entity = (row[iEntity] ?? "").trim();
    if (!entity) continue;
    const metric = iMetric >= 0 ? (row[iMetric] ?? "").trim() : "";
    sampled++;
    entityExamples.push(entity);
    if (metric) metricExamples.push(metric);
    const verdict = classifyRow(entity, metric);
    if (!verdict) {
      unclassified++;
      continue;
    }
    const c = counts[verdict.kind] ?? { total: 0, exact: 0 };
    c.total++;
    if (verdict.exact) c.exact++;
    counts[verdict.kind] = c;
  }

  if (sampled === 0) {
    return detection(
      null,
      "ambiguous",
      "It has the shared long-CSV header (entity_ref / metric / as_of_date / value) but no data "
        + "rows were readable, and eight of the eleven kinds share that exact header.",
      [],
      "none",
    );
  }

  const ranked = (Object.keys(counts) as GoldenKind[]).sort(
    (a, b) => counts[b].total - counts[a].total || counts[b].exact - counts[a].exact,
  );
  if (ranked.length === 0) {
    return detection(
      null,
      "ambiguous",
      `None of the ${sampled} entity_ref value(s) read — ${samples(entityExamples)} — match a `
        + "known portfolio, benchmark, peer-house, sector or cost reference, so the kind cannot be "
        + "worked out from the contents.",
      [],
      "none",
    );
  }

  const top = ranked[0];
  const topShare = counts[top].total / sampled;
  const runnerShare = ranked.length > 1 ? counts[ranked[1]].total / sampled : 0;
  const evidence = `entity_refs like ${samples(entityExamples)}`
    + (metricExamples.length ? ` with metric ${samples(metricExamples, 2)}` : "");
  const scope = partialSample ? `the first ${sampled} rows read` : `all ${sampled} rows`;

  // 'certain' requires a by-construction signal (at least one exact match) AND near-unanimity.
  if (counts[top].exact > 0 && topShare >= 0.9 && runnerShare <= 0.05) {
    return detection(
      top,
      "certain",
      `${evidence} — that is this kind's own vocabulary, and it holds across ${scope}.`,
      ranked.slice(1, 3),
      "content",
    );
  }

  if (topShare >= 0.6) {
    return detection(
      top,
      "likely",
      counts[top].exact === 0
        ? `${evidence} — free-text identities with a return metric, which is what a fund-level file `
          + `looks like, but nothing in ${scope} proves it. Worth a glance.`
        : `${evidence} — mostly this kind's vocabulary (${counts[top].total} of ${sampled} rows), `
          + "but not all of it. Worth a glance.",
      ranked.slice(1, 3),
      "content",
    );
  }

  return detection(
    null,
    "ambiguous",
    `Its rows point at more than one kind — ${evidence}, and ${scope} split across `
      + `${ranked.length} different vocabularies`
      + (unclassified ? ` with ${unclassified} row(s) matching none of them` : "")
      + ", so the contents alone do not settle it.",
    ranked.slice(0, 3),
    "content",
  );
}

// ----------------------------------------------------------------------------
// LAYER 4 — FILENAME, as a WEAK TIEBREAK ONLY.
//
// It never produces a kind on its own: FE names its exports with an opaque GUID that carries no
// report identity at all (index.ts:54), and a filename is the one part of a drop a human can get
// wrong without the file changing. It is applied ONLY to choose between alternatives the content
// layers already put in play, and only when exactly one of them matches.
//
// Deliberately NOT applied to the FE-native both-families case: there the right answer is "drop
// it twice", and letting the filename pick one would quietly cost the owner the other half.
// ----------------------------------------------------------------------------
const FILENAME_HINTS: { kind: GoldenKind; rx: RegExp }[] = [
  { kind: "holdings_base_snapshot", rx: /(base[_ -]?snapshot|opening|snapshot)/i },
  { kind: "holdings_deltas", rx: /(delta|weight[_ -]?change)/i },
  { kind: "ba_trades", rx: /(trade|blotter)/i },
  { kind: "fe_benchmark_constituent_weights", rx: /constituent/i },
  { kind: "fe_sector_weights", rx: /sector[_ -]?weight/i },
  { kind: "sector_mappings", rx: /(mapping|look[_ -]?through|lookthrough)/i },
  { kind: "composite_constituents", rx: /(composite|peer|house)/i },
  { kind: "costs", rx: /(cost|charge|amc|ocf)/i },
  { kind: "fe_fund_returns", rx: /fund/i },
  { kind: "fe_benchmark_returns", rx: /(benchmark|bench|afi|arc)/i },
  { kind: "fe_portfolio_returns", rx: /(portfolio|model)/i },
];

function filenameTiebreak(filename: string, candidates: GoldenKind[]): GoldenKind | null {
  const name = (filename ?? "").trim();
  if (!name || candidates.length < 2) return null;
  const hits = FILENAME_HINTS.filter((h) => candidates.includes(h.kind) && h.rx.test(name));
  return hits.length === 1 ? hits[0].kind : null;
}

// ----------------------------------------------------------------------------
// The whole thing.
// ----------------------------------------------------------------------------
export function detectGoldenKind(
  filename: string,
  csvText: string,
  options: DetectOptions = {},
): KindDetection {
  const maxLines = options.maxLines ?? DETECT_MAX_LINES;
  const text = (csvText ?? "").replace(/^﻿/, "");
  if (!text.trim()) {
    return detection(null, "ambiguous", "The file is empty, so there is nothing to read.", [], "none");
  }

  const { grid, hitRowCap } = parseCsvHead(text, maxLines);
  // The sample is partial if the caller only handed over a head slice, or if we stopped at the
  // row cap. Both mean an ABSENCE in the sample proves nothing.
  const partialSample = !!options.partial || hitRowCap;

  if (grid.length === 0) {
    return detection(null, "ambiguous", "No CSV rows could be read from the file.", [], "none");
  }

  // Layer 2 first in code (it decides WHERE the header row is), but disjoint from layer 1: an
  // FE file's row 0 is the banner, so it can never satisfy a header contract.
  if (sniffFeFormat(grid)) return detectFromFeNative(grid, partialSample);

  const headers = (grid[0] ?? []).map(normHeader);

  // Layer 1.
  const byHeader = detectFromHeader(headers);
  if (byHeader && byHeader.confidence !== "ambiguous") return byHeader;

  // Layer 3.
  const byContent = byHeader ?? detectFromContent(grid, headers, partialSample);
  if (byContent.confidence !== "ambiguous") return byContent;

  // Layer 4 — weak tiebreak among what is already in play. Promoted to 'likely' at most: a
  // filename is evidence of INTENT, never of content.
  const picked = filenameTiebreak(filename, byContent.alternatives);
  if (picked) {
    return detection(
      picked,
      "likely",
      `${byContent.reason} The file name "${filename}" points at one of them — that is only a hint, `
        + "so check it before dropping.",
      byContent.alternatives,
      "filename",
    );
  }

  return byContent;
}

/** Re-exported so a consumer can type a detection result without a second import. */
export type { GoldenKind };
